import { createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import adminModel, { Admin, AdminState, CreateAdminDto } from '@/core/models/admin.model'
import { CreateEstablishmentDto, Establishment, UpdateEstablishmentDto } from '@/core/models/establishment.model'
import { CreateStaffDto, Staff } from '@/core/models/staff.model'
import { SearchRequestOptions } from '@/core/types/request'

import { RootState } from '@/redux/rootReducer'
import { EstablishmentSubscription, SubscriptionType } from '@/utils/subscription/types'

export type AdminReducerState = AdminState

export const establishmentAdapter = createEntityAdapter({ selectId: (establishment: Establishment) => establishment._id })
const establishmentSelector = establishmentAdapter.getSelectors((state: RootState) => state.admin.establishments)

export const staffAdapter = createEntityAdapter({ selectId: (staff: Staff) => staff._id })
const staffSelector = staffAdapter.getSelectors((state: RootState) => state.admin.staffs)

export const selectEstablishments = establishmentSelector.selectAll
export const selectEstablishmentById = establishmentSelector.selectById
export const selectStaffs = staffSelector.selectAll
export const selectStaffById = staffSelector.selectById
export const selectNewlyCreatedAdminStaffId = (state: RootState) => state.admin.newlyCreatedItemId
export const selectDoctors = createSelector(selectStaffs, (staffs) => staffs.filter((staff) => staff.status === 'MEDECIN'))

export const selectAdminListRequestTracker = (state: RootState) => state.admin.requestTracker.list
export const selectAdminCreateRequestTracker = (state: RootState) => state.admin.requestTracker.create
export const selectAdminUpdateRequestTracker = (state: RootState) => state.admin.requestTracker.update
export const selectAdminDeleteRequestTracker = (state: RootState) => state.admin.requestTracker.delete
export const selectOrdoclicEntityCreateRequestTracker = (state: RootState) => state.admin.requestTracker.create
export const selectAssignProfessionalsRequestTracker = (state: RootState) => state.admin.requestTracker.update


export const selectAdminSearchResult = (state: RootState) => state.admin.search.result
const initialState: AdminReducerState = {
  ...adminModel.getInitialState(),
}

const adminSlice = createSlice({
  name: adminModel.reducerName,
  initialState,
  reducers: {
    createAdmin(state, action: PayloadAction<CreateAdminDto>) {
      state.requestTracker.create.loading = true
    },
    createAdminSuccess(state, action: PayloadAction<Admin>) {
      const admin = action.payload
      if (admin.establishment) {
        const establishment = state.establishments.entities[admin.establishment._id]
        if (establishment) establishmentAdapter.updateOne(state.establishments, { id: admin.establishment._id, changes: { ...establishment, admin } })
      }
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createAdminFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    createEstablishment(state, action: PayloadAction<CreateEstablishmentDto>) {
      state.requestTracker.create.loading = true
    },
    createEstablishmentSuccess(state, action: PayloadAction<Establishment>) {
      const establishment = action.payload
      establishmentAdapter.addOne(state.establishments, establishment)
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createEstablishmentFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    deleteEstablishment(state, action: PayloadAction<string>) {
      state.requestTracker.delete.loading = true
    },
    deleteEstablishmentSuccess(state, action: PayloadAction<string>) {
      const establishmentId = action.payload
      establishmentAdapter.removeOne(state.establishments, establishmentId)
      state.requestTracker.delete.error = null
      state.requestTracker.delete.loading = false
    },
    deleteEstablishmentFailure(state, action) {
      state.requestTracker.delete.error = action.payload
      state.requestTracker.delete.loading = false
    },
    getEstablishments(state) {
      state.requestTracker.list.loading = true
    },
    getEstablishmentsSuccess(state, action: PayloadAction<Establishment[]>) {
      establishmentAdapter.setAll(state.establishments, action.payload)
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    getEstablishmentsFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },
    getEstablishment(state) {
      state.requestTracker.get.loading = true
    },
    getEstablishmentSuccess(state, action: PayloadAction<Establishment>) {
      establishmentAdapter.setOne(state.establishments, action.payload)
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    getEstablishmentFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },

    updateEstablishment(state, action: PayloadAction<{ etablissementId: string; updateEstablishmentDto: UpdateEstablishmentDto }>) {
      state.requestTracker.get.loading = true
    },
    updateEstablishmentSuccess(state, action: PayloadAction<Establishment>) {
      const etablissement = action.payload
      establishmentAdapter.updateOne(state.establishments, { id: etablissement._id, changes: etablissement })
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateEstablishmentFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },

    getStaffs: {
      reducer: (state, action: PayloadAction<{ filter: Record<string, any>; options?: SearchRequestOptions }>) => {
        state.requestTracker.list.loading = true
      },
      prepare: (filter: Record<string, any>, options?: SearchRequestOptions) => ({
        payload: { filter, options },
      }),
    },
    getStaffsSuccess(state, action: PayloadAction<{ staffs: Staff[]; totalCount: number }>) {
      staffAdapter.setAll(state.staffs, action.payload.staffs)
      state.search.result.totalCount = action.payload.totalCount

      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    getStaffsFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },
    deleteStaff: {
      reducer: (state, action: PayloadAction<{ staffId: string; associatedData?: string[] }>) => {
        state.requestTracker.delete.loading = true
      },
      prepare: (staffId: string, associatedData?: string[]) => ({
        payload: { staffId, associatedData },
      }),
    },
    deleteStaffSuccess(state, action: PayloadAction<Staff>) {
      staffAdapter.removeOne(state.staffs, action.payload._id)
      state.requestTracker.delete.error = null
      state.requestTracker.delete.loading = false
    },
    deleteStaffFailure(state, action) {
      state.requestTracker.delete.error = action.payload
      state.requestTracker.delete.loading = false
    },
    addStaff(state, action: PayloadAction<CreateStaffDto>) {
      state.requestTracker.create.loading = true
    },
    addStaffSuccess(state, action: PayloadAction<Staff>) {
      staffAdapter.addOne(state.staffs, action.payload)
      state.newlyCreatedItemId = action.payload._id
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    addStaffFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    testPlugin(state, action: PayloadAction<any>) {
      state.requestTracker.update.loading = true
    },
    testPluginSuccess(state, action: PayloadAction<boolean>) {
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    testPluginFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    addPlugin(state, action: PayloadAction<any>) {
      state.requestTracker.create.loading = true
    },
    addPluginSuccess(state, action: PayloadAction<Establishment>) {
      const etablissement = action.payload
      establishmentAdapter.updateOne(state.establishments, { id: etablissement._id, changes: etablissement })
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    addPluginFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    deletePlugin(state, action: PayloadAction<CreateStaffDto>) {
      state.requestTracker.delete.loading = true
    },
    deletePluginSuccess(state, action: PayloadAction<Establishment>) {
      const etablissement = action.payload
      establishmentAdapter.updateOne(state.establishments, { id: etablissement._id, changes: etablissement })
      state.requestTracker.delete.error = null
      state.requestTracker.delete.loading = false
    },
    deletePluginFailure(state, action) {
      state.requestTracker.delete.error = action.payload
      state.requestTracker.delete.loading = false
    },
    updateEstablishmentSubscription(state, action: PayloadAction<{ etablissementId: string; subscription: EstablishmentSubscription }>) {
      state.requestTracker.update.loading = true
    },
    updateEstablishmentSubscriptionSuccess(state, action: PayloadAction<Establishment>) {
      const etablissement = action.payload
      establishmentAdapter.updateOne(state.establishments, { id: etablissement._id, changes: etablissement })
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateEstablishmentSubscriptionFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    updateStaffSubscription(state, action: PayloadAction<{ staffId: string; subscription: SubscriptionType }>) {
      state.requestTracker.update.loading = true
    },
    updateStaffSubscriptionSuccess(state, action: PayloadAction<Staff>) {
      const staff = action.payload
      staffAdapter.updateOne(state.staffs, { id: staff._id, changes: staff })
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateStaffSubscriptionFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    createOrdoclicAccount(state, action: PayloadAction<string>) {
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = true
    },
    createOrdoclicAccountSuccess(state, action: PayloadAction<Staff>) {
      const staff = action.payload
      staffAdapter.updateOne(state.staffs, { id: staff._id, changes: staff })
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    createOrdoclicAccountFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    createOrdoclicEntity: (state, action: PayloadAction<{ establishmentId: string; name: string; finess?: string }>) => {
      state.requestTracker.create.loading = true;
    },
    createOrdoclicEntitySuccess: (state, action: PayloadAction<Establishment>) => {
      establishmentAdapter.upsertOne(state.establishments, action.payload);
      state.requestTracker.create.loading = false;
      state.requestTracker.create.error = null;
    },
    createOrdoclicEntityFailure: (state, action) => {
      state.requestTracker.create.loading = false;
      state.requestTracker.create.error = action.payload;
    },
    assignProfessionalsToOrdoclicEntity: (state, action: PayloadAction<string>) => {
      state.requestTracker.update.loading = true;
    },
    assignProfessionalsToOrdoclicEntitySuccess: (state, action: PayloadAction<Establishment>) => {
      establishmentAdapter.upsertOne(state.establishments, action.payload);
      state.requestTracker.update.loading = false;
      state.requestTracker.update.error = null;
    },
    assignProfessionalsToOrdoclicEntityFailure: (state, action) => {
      state.requestTracker.update.loading = false;
      state.requestTracker.update.error = action.payload;
    },
  },
})

export const {
  createAdmin,
  createAdminSuccess,
  createAdminFailure,
  createEstablishment,
  createEstablishmentSuccess,
  createEstablishmentFailure,
  deleteEstablishment,
  deleteEstablishmentSuccess,
  deleteEstablishmentFailure,
  getEstablishments,
  getEstablishmentsSuccess,
  getEstablishmentsFailure,
  getEstablishment,
  getEstablishmentSuccess,
  getEstablishmentFailure,
  getStaffs,
  getStaffsSuccess,
  getStaffsFailure,
  deleteStaff,
  deleteStaffSuccess,
  deleteStaffFailure,
  addStaff,
  addStaffSuccess,
  addStaffFailure,
  testPlugin,
  testPluginSuccess,
  testPluginFailure,
  addPlugin,
  addPluginSuccess,
  addPluginFailure,
  deletePlugin,
  deletePluginSuccess,
  deletePluginFailure,
  updateEstablishment,
  updateEstablishmentSuccess,
  updateEstablishmentFailure,
  updateEstablishmentSubscription,
  updateEstablishmentSubscriptionSuccess,
  updateEstablishmentSubscriptionFailure,
  updateStaffSubscription,
  updateStaffSubscriptionSuccess,
  updateStaffSubscriptionFailure,
  createOrdoclicAccount,
  createOrdoclicAccountSuccess,
  createOrdoclicAccountFailure,
  createOrdoclicEntity,
  createOrdoclicEntitySuccess,
  createOrdoclicEntityFailure,
  assignProfessionalsToOrdoclicEntity,
  assignProfessionalsToOrdoclicEntitySuccess,
  assignProfessionalsToOrdoclicEntityFailure,
} = adminSlice.actions

export default adminSlice.reducer
